import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import content from './content.json';
import { BASE, fmtDate } from './Layout';

export function ArticleCard({ article }) {
  return (
    <Link to={`${BASE}/research/${article.slug}`} className="group grid gap-3 md:grid-cols-[180px_1fr_auto] items-start py-8 border-t ibl-line">
      <div className="ibl-mono text-[10px] ibl-muted leading-relaxed">
        <time dateTime={article.date}>{fmtDate(article.date)}</time><br />
        <span className="ibl-faint">{article.minutes} min read</span>
      </div>
      <div className="max-w-2xl">
        <h3 className="text-[24px] sm:text-[28px] font-medium tracking-[-0.025em] leading-tight group-hover:underline underline-offset-4 decoration-1">{article.title}</h3>
        {article.summary && <p className="mt-2 text-[15px] ibl-muted leading-relaxed">{article.summary}</p>}
        {article.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">{article.tags.map((t) => <span key={t} className="ibl-chip">{t}</span>)}</div>
        )}
      </div>
      <span className="hidden md:block text-[22px] ibl-faint transition-transform group-hover:translate-x-1" aria-hidden="true">→</span>
    </Link>
  );
}

export default function Research() {
  const { articles } = content;
  return (
    <section className="mx-auto max-w-6xl px-6 pt-20 pb-10">
      <p className="ibl-mono ibl-muted mb-5">Research</p>
      <h1 className="text-[40px] sm:text-[56px] font-medium tracking-[-0.035em] leading-[1] max-w-3xl">
        Where we are going, <span className="ibl-faint">and why.</span>
      </h1>
      <p className="mt-6 max-w-xl text-[17px] ibl-muted leading-relaxed">
        Long-form writing on the lab's direction: what we think the body could become, which problems we pick, and how we work on them.
      </p>
      <div className="mt-14 border-b ibl-line">
        {articles.map((a) => <ArticleCard key={a.slug} article={a} />)}
      </div>
    </section>
  );
}

export function Article() {
  const { slug } = useParams();
  const article = content.articles.find((a) => a.slug === slug);
  if (!article) return <Navigate to={`${BASE}/research`} replace />;
  const i = content.articles.indexOf(article);
  const next = content.articles[i + 1] || content.articles[0];

  return (
    <article className="mx-auto max-w-6xl px-6 pt-20 pb-10">
      <div className="grid gap-10 lg:grid-cols-[180px_1fr]">
        <aside className="ibl-mono text-[10px] ibl-muted leading-loose lg:sticky lg:top-24 self-start">
          <Link to={`${BASE}/research`} className="hover:text-[var(--ibl-ink)]">← Research</Link><br />
          <time dateTime={article.date}>{fmtDate(article.date)}</time><br />
          <span className="ibl-faint">{article.minutes} min read</span>
          {article.tags.length > 0 && <div className="mt-4 flex flex-wrap gap-2 lg:flex-col lg:items-start">{article.tags.map((t) => <span key={t} className="ibl-chip">{t}</span>)}</div>}
        </aside>
        <div className="max-w-[680px]">
          <h1 className="text-[36px] sm:text-[52px] font-medium tracking-[-0.035em] leading-[1.02]">{article.title}</h1>
          {article.summary && <p className="mt-6 text-[19px] ibl-muted leading-relaxed">{article.summary}</p>}
          <div className="ibl-prose mt-12" dangerouslySetInnerHTML={{ __html: article.html }} />
          {next !== article && (
            <Link to={`${BASE}/research/${next.slug}`} className="group mt-20 block border-t ibl-line pt-6">
              <span className="ibl-mono text-[10px] ibl-faint">Next</span>
              <span className="mt-1 block text-[22px] font-medium tracking-[-0.02em] group-hover:underline underline-offset-4 decoration-1">{next.title} →</span>
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
